import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPortfolioTiers, getPortfolioZoomLabel, PORTFOLIO_PRESETS,
  unitBoundaries, topTierLabel, middleTierLabel,
} from './zoomUtils';
import './PortfolioGanttView.css';

const HEADER_HEIGHT = 56;
const TOP_TIER_HEIGHT = 28;
const MIDDLE_TIER_HEIGHT = 28;
const ROW_HEIGHT = 36;
const BAR_HEIGHT = 20;
const NAME_PANE_WIDTH = 240;
const NAME_PANE_MIN = 120;
const NAME_PANE_MAX = 600;
const MIN_PX = 0.3;
const MAX_PX = 30;
const SPLITTER_KEY = 'hrapp.portfolio.layout.splitter';
const MODE_KEY = 'hrapp.portfolio.layout.mode'; // VW-S5
const PHASE_BAR_COLOR = '#00897b'; // VW-S5: teal — distinct from project status colors

const statusColor = (status) => {
  switch (status) {
    case 'Active': return '#1976d2';
    case 'OnHold': return '#fb8c00';
    case 'Completed': return '#43a047';
    case 'Cancelled': return '#bdbdbd';
    case 'Draft': return '#9e9e9e';
    default: return '#1976d2';
  }
};

const fmtD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
};

const clampPct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

/**
 * PL-S4/PL-S5: portfolio Gantt — one bar per project on a multi-tier timeline.
 * Presentational; receives `projects` via props (normalized rows from ProjectListPage).
 * `navigate` and `onCreate` are optional callbacks (default no-op for standalone use).
 */
const PortfolioGanttView = ({ projects = [], navigate = () => {}, onCreate = () => {} }) => {
  const [pxPerDay, setPxPerDay] = useState(4); // Quarter preset
  const [tooltip, setTooltip] = useState(null);
  const timelineRef = useRef(null);
  const nameRowsRef = useRef(null);

  // VW-S5: Project / Phase rendering mode, persisted per browser.
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(MODE_KEY);
      if (stored === 'project' || stored === 'phase') return stored;
    } catch {}
    return 'project';
  });
  useEffect(() => {
    try { localStorage.setItem(MODE_KEY, mode); } catch {}
  }, [mode]);

  // PL-S5: resizable splitter — name-pane width persisted to localStorage.
  const [namePaneWidth, setNamePaneWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SPLITTER_KEY));
    return saved >= NAME_PANE_MIN && saved <= NAME_PANE_MAX ? saved : NAME_PANE_WIDTH;
  });
  const widthRef = useRef(namePaneWidth);

  // Projects sorted by Start ascending; date-less projects sink to the bottom.
  const sortedProjects = useMemo(() => {
    const startMs = (p) => {
      const s = p.startDate ? new Date(p.startDate).getTime() : NaN;
      return isNaN(s) ? Number.POSITIVE_INFINITY : s;
    };
    return [...projects].sort((a, b) => startMs(a) - startMs(b));
  }, [projects]);

  // VW-S5: row model — `project` mode is one row per project (today's
  // behavior, unchanged). `phase` mode expands each project into a
  // header row + one row per phase.
  const rows = useMemo(() => {
    if (mode === 'project') {
      return sortedProjects.map((p) => ({ kind: 'project', project: p }));
    }
    const out = [];
    for (const p of sortedProjects) {
      out.push({ kind: 'project-header', project: p });
      for (const ph of (p.phases || [])) {
        out.push({ kind: 'phase', project: p, phase: ph });
      }
    }
    return out;
  }, [mode, sortedProjects]);

  // Timeline window — earliest start to latest end across dated projects.
  const { timelineStart, timelineEnd } = useMemo(() => {
    const starts = [];
    const ends = [];
    for (const p of projects) {
      if (p.startDate) { const s = new Date(p.startDate); if (!isNaN(s)) starts.push(s.getTime()); }
      if (p.endDate) { const e = new Date(p.endDate); if (!isNaN(e)) ends.push(e.getTime()); }
    }
    let start;
    let end;
    if (starts.length && ends.length) {
      start = new Date(Math.min(...starts));
      end = new Date(Math.max(...ends));
    } else {
      start = new Date();
      end = new Date();
      end.setMonth(end.getMonth() + 6);
    }
    // padding
    start = new Date(start); start.setDate(start.getDate() - 14);
    end = new Date(end); end.setDate(end.getDate() + 14);
    return { timelineStart: start, timelineEnd: end };
  }, [projects]);

  const totalDays = Math.max(1, Math.ceil((timelineEnd - timelineStart) / 86400000));
  const totalWidth = Math.max(totalDays * pxPerDay, 200);
  const totalHeight = HEADER_HEIGHT + rows.length * ROW_HEIGHT;

  const dayToX = useCallback((date) => {
    return ((new Date(date).getTime() - timelineStart.getTime()) / 86400000) * pxPerDay;
  }, [timelineStart, pxPerDay]);

  const tiers = useMemo(() => getPortfolioTiers(pxPerDay), [pxPerDay]);
  const zoomLabel = useMemo(() => getPortfolioZoomLabel(pxPerDay), [pxPerDay]);

  const topTier = useMemo(() => {
    return unitBoundaries(tiers.top, timelineStart, timelineEnd).map((seg, i) => {
      const x1 = dayToX(seg.start < timelineStart ? timelineStart : seg.start);
      const x2 = dayToX(seg.end > timelineEnd ? timelineEnd : seg.end);
      return {
        key: `t-${seg.start.getTime()}`,
        label: topTierLabel(tiers.top, seg.start),
        x: x1, width: Math.max(x2 - x1, 0), even: i % 2 === 0,
      };
    });
  }, [tiers.top, timelineStart, timelineEnd, dayToX]);

  const middleTier = useMemo(() => {
    return unitBoundaries(tiers.middle, timelineStart, timelineEnd).map((seg, i) => {
      const x1 = dayToX(seg.start < timelineStart ? timelineStart : seg.start);
      const x2 = dayToX(seg.end > timelineEnd ? timelineEnd : seg.end);
      return {
        key: `m-${seg.start.getTime()}`,
        label: middleTierLabel(tiers.middle, seg.start),
        x: x1, width: Math.max(x2 - x1, 0), even: i % 2 === 0,
      };
    });
  }, [tiers.middle, timelineStart, timelineEnd, dayToX]);

  const gridLines = useMemo(() => middleTier.map((s) => s.x), [middleTier]);
  const todayX = dayToX(new Date());

  // Pin-point zoom — preserve the calendar date at viewport centre.
  const zoomTo = useCallback((newPx) => {
    const clamped = Math.min(MAX_PX, Math.max(MIN_PX, newPx));
    const el = timelineRef.current;
    if (!el) { setPxPerDay(clamped); return; }
    const focalX = el.scrollLeft + el.clientWidth / 2;
    const focalDays = pxPerDay > 0 ? focalX / pxPerDay : 0;
    const newScrollLeft = focalDays * clamped - el.clientWidth / 2;
    setPxPerDay(clamped);
    requestAnimationFrame(() => {
      if (timelineRef.current) timelineRef.current.scrollLeft = Math.max(0, newScrollLeft);
    });
  }, [pxPerDay]);

  const fitToPortfolio = useCallback(() => {
    const el = timelineRef.current;
    if (!el) return;
    const days = Math.max(1, (timelineEnd - timelineStart) / 86400000);
    const targetPx = Math.max(MIN_PX, Math.min(MAX_PX, el.clientWidth / days));
    setPxPerDay(targetPx);
    requestAnimationFrame(() => {
      if (timelineRef.current) timelineRef.current.scrollLeft = 0;
    });
  }, [timelineStart, timelineEnd]);

  const scrollToToday = useCallback(() => {
    const el = timelineRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, dayToX(new Date()) - el.clientWidth / 2);
  }, [dayToX]);

  const onBarEnter = useCallback((e, proj) => {
    setTooltip({ x: e.clientX + 12, y: e.clientY + 12, proj });
  }, []);
  const onBarLeave = useCallback(() => setTooltip(null), []);
  const onBarMove = useCallback((e) => {
    setTooltip((t) => (t ? { ...t, x: e.clientX + 12, y: e.clientY + 12 } : null));
  }, []);

  const goToProject = useCallback((id) => {
    if (id != null) navigate(`/projects/${id}`);
  }, [navigate]);

  // PL-S5: keep the name pane scrolled in lock-step with the timeline.
  const onTimelineScroll = useCallback((e) => {
    if (nameRowsRef.current) {
      nameRowsRef.current.style.transform = `translateY(${-e.target.scrollTop}px)`;
    }
  }, []);

  // PL-S5: splitter drag — resize the name pane, persist on release.
  const onSplitterDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => {
      const next = Math.min(NAME_PANE_MAX, Math.max(NAME_PANE_MIN, startW + (ev.clientX - startX)));
      widthRef.current = next;
      setNamePaneWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(SPLITTER_KEY, String(widthRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => { widthRef.current = namePaneWidth; }, [namePaneWidth]);

  // PL-S5: empty state — friendly CTA when there are no projects.
  if (!projects.length) {
    return (
      <div className="pgv pgv-empty">
        <div className="pgv-empty-box">
          <div className="pgv-empty-title">No projects yet</div>
          <div className="pgv-empty-sub">Create a project to see it on the portfolio timeline.</div>
          <button type="button" className="pgv-empty-cta" onClick={onCreate}>
            + Add your first project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pgv">
      {/* Zoom toolbar */}
      <div className="pgv-toolbar">
        <div className="pgv-zoom-group">
          {PORTFOLIO_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className={`pgv-zoom-btn${zoomLabel === p.label ? ' active' : ''}`}
              onClick={() => zoomTo(p.px)}
            >{p.label}</button>
          ))}
        </div>
        <input
          className="pgv-zoom-slider"
          type="range"
          min={MIN_PX}
          max={MAX_PX}
          step={0.1}
          value={pxPerDay}
          onChange={(e) => zoomTo(Number(e.target.value))}
          aria-label="Zoom level (pixels per day)"
        />
        <span className="pgv-zoom-label">{pxPerDay.toFixed(1)} px/day</span>
        <button className="pgv-tb-btn" type="button" onClick={fitToPortfolio}
          data-testid="pgv-fit">⊞ Fit to Portfolio</button>
        <button className="pgv-tb-btn" type="button" onClick={scrollToToday}
          data-testid="pgv-today">↑ Today</button>
        {/* VW-S5: Project / Phase view toggle */}
        <div className="pgv-mode-toggle" role="group" aria-label="Portfolio rendering mode">
          <button
            type="button"
            className={mode === 'project' ? 'active' : ''}
            onClick={() => setMode('project')}
            data-testid="pgv-mode-project"
            title="One bar per project"
          >Project</button>
          <button
            type="button"
            className={mode === 'phase' ? 'active' : ''}
            onClick={() => setMode('phase')}
            data-testid="pgv-mode-phase"
            title="One row per phase, grouped by project"
          >Phase</button>
        </div>
      </div>

      {/* a11y: announce zoom changes to assistive tech */}
      <div className="pgv-sr-only" aria-live="polite" data-testid="pgv-zoom-live">
        {`Zoom: ${zoomLabel}`}
      </div>

      <div className="pgv-body">
        {/* Project name pane */}
        <div className="pgv-name-pane" style={{ width: namePaneWidth }}>
          <div className="pgv-name-head" style={{ height: HEADER_HEIGHT }}>Projects</div>
          <div className="pgv-name-rows" ref={nameRowsRef}>
            {rows.map((r, i) => {
              const isPhase = r.kind === 'phase';
              const isHeader = r.kind === 'project-header';
              const label = isPhase ? r.phase.name : r.project.name;
              const cls = `pgv-name-row${isHeader ? ' project-header' : ''}${isPhase ? ' phase' : ''}`;
              return (
                <div
                  key={isPhase
                    ? `${r.project.id}-ph-${r.phase.sortOrder}`
                    : isHeader ? `${r.project.id}-h` : r.project.id}
                  className={cls}
                  style={{ height: ROW_HEIGHT }}
                  data-testid="pgv-name-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => goToProject(r.project.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToProject(r.project.id); } }}
                >
                  <span className="pgv-name-text" title={label}>{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Draggable splitter */}
        <div
          className="pgv-splitter"
          data-testid="pgv-splitter"
          onMouseDown={onSplitterDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize project name column"
        />

        {/* Timeline */}
        <div className="pgv-timeline" ref={timelineRef} onScroll={onTimelineScroll}>
          <svg width={totalWidth} height={totalHeight} style={{ display: 'block' }}>
            {/* Alternating row backgrounds */}
            {rows.map((_, i) => i % 2 === 1 && (
              <rect key={`bg-${i}`} x={0} y={HEADER_HEIGHT + i * ROW_HEIGHT}
                width={totalWidth} height={ROW_HEIGHT} fill="#f9f9f9" />
            ))}

            {/* Vertical grid lines */}
            {gridLines.map((x, i) => (
              <line key={`grid-${i}`} x1={x} y1={HEADER_HEIGHT} x2={x} y2={totalHeight}
                stroke="#ececec" strokeWidth={1} />
            ))}

            {/* Header background */}
            <rect x={0} y={0} width={totalWidth} height={HEADER_HEIGHT} fill="#eceff1" />

            {/* Top tier */}
            {topTier.map((seg) => (
              <g key={seg.key}>
                <rect x={seg.x} y={0} width={seg.width} height={TOP_TIER_HEIGHT}
                  fill={seg.even ? '#e3eaf3' : '#dde6f0'} />
                <line x1={seg.x} y1={0} x2={seg.x} y2={TOP_TIER_HEIGHT} stroke="#c8d4e0" strokeWidth={1} />
                {seg.width > 24 && (
                  <text x={seg.x + 6} y={19} fontSize={12} fontWeight="600" fill="#37474f">{seg.label}</text>
                )}
              </g>
            ))}

            {/* Middle tier */}
            {middleTier.map((seg) => (
              <g key={seg.key}>
                <rect x={seg.x} y={TOP_TIER_HEIGHT} width={seg.width} height={MIDDLE_TIER_HEIGHT}
                  fill={seg.even ? '#eef2f7' : '#f3f6fa'} />
                <line x1={seg.x} y1={TOP_TIER_HEIGHT} x2={seg.x} y2={HEADER_HEIGHT} stroke="#c8d4e0" strokeWidth={1} />
                {seg.label && seg.width > 14 && (
                  <text x={seg.x + 4} y={47} fontSize={10} fill="#546e7a">{seg.label}</text>
                )}
              </g>
            ))}

            <line x1={0} y1={HEADER_HEIGHT} x2={totalWidth} y2={HEADER_HEIGHT} stroke="#b0bec5" strokeWidth={1} />

            {/* Bars — project (project mode) or per-phase (phase mode) */}
            {rows.map((r, i) => {
              const rowY = HEADER_HEIGHT + i * ROW_HEIGHT;
              const barTop = rowY + (ROW_HEIGHT - BAR_HEIGHT) / 2;
              if (r.kind === 'project') {
                const proj = r.project;
                if (!proj.startDate || !proj.endDate) return null;
                const x1 = dayToX(proj.startDate);
                const x2 = dayToX(proj.endDate);
                const w = Math.max(x2 - x1, 2);
                const color = statusColor(proj.status);
                const hours = Number(proj.totalPlannedHours) || 0;
                const fte = Number(proj.equivalentFteAt75) || 0;
                const pct = clampPct(proj.completionPercent);
                const fillW = (w * pct) / 100;
                const label = `${proj.name} · ${hours}h · ${fte} FTE @75%`;
                const ariaLabel = `${proj.name}, ${hours} hours, ${fte} FTE at 75%, ${pct}% complete`;
                return (
                  <g key={proj.id}
                    data-testid="pgv-bar"
                    role="button"
                    aria-label={ariaLabel}
                    onMouseEnter={(e) => onBarEnter(e, proj)}
                    onMouseLeave={onBarLeave}
                    onMouseMove={onBarMove}
                    onClick={() => goToProject(proj.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* muted base + progress fill */}
                    <rect x={x1} y={barTop} width={w} height={BAR_HEIGHT} rx={3}
                      fill={color} fillOpacity={0.55} />
                    {fillW > 0 && (
                      <rect data-testid="pgv-progress" x={x1} y={barTop} width={fillW}
                        height={BAR_HEIGHT} rx={3} fill={color} />
                    )}
                    <rect x={x1} y={barTop} width={w} height={BAR_HEIGHT} rx={3}
                      fill="none" stroke={color} strokeWidth={1} />
                    {w > 100 && (
                      <text x={x1 + 6} y={barTop + BAR_HEIGHT / 2 + 4} fontSize={10} fill="#fff"
                        style={{ pointerEvents: 'none' }}>{label}</text>
                    )}
                  </g>
                );
              }
              if (r.kind === 'phase') {
                const ph = r.phase;
                if (!ph.startDate || !ph.endDate) return null;
                const x1 = dayToX(ph.startDate);
                const x2 = dayToX(ph.endDate);
                const w = Math.max(x2 - x1, 2);
                const pct = clampPct(ph.completionPercent);
                const fillW = (w * pct) / 100;
                const ariaLabel = `${r.project.name} — ${ph.name}, ${pct}% complete`;
                return (
                  <g key={`${r.project.id}-ph-${ph.sortOrder}`}
                    data-testid="pgv-phase-bar"
                    role="button"
                    aria-label={ariaLabel}
                    onClick={() => goToProject(r.project.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect x={x1} y={barTop} width={w} height={BAR_HEIGHT} rx={3}
                      fill={PHASE_BAR_COLOR} fillOpacity={0.55} />
                    {fillW > 0 && (
                      <rect x={x1} y={barTop} width={fillW}
                        height={BAR_HEIGHT} rx={3} fill={PHASE_BAR_COLOR} />
                    )}
                    <rect x={x1} y={barTop} width={w} height={BAR_HEIGHT} rx={3}
                      fill="none" stroke={PHASE_BAR_COLOR} strokeWidth={1} />
                    {w > 100 && (
                      <text x={x1 + 6} y={barTop + BAR_HEIGHT / 2 + 4} fontSize={10} fill="#fff"
                        style={{ pointerEvents: 'none' }}>{ph.name}</text>
                    )}
                  </g>
                );
              }
              // project-header row: no bar
              return null;
            })}

            {/* Today line + badge */}
            <line x1={todayX} y1={0} x2={todayX} y2={totalHeight}
              stroke="#e53935" strokeWidth={2} strokeDasharray="4 4" data-testid="pgv-today-line" />
            <g pointerEvents="none">
              <rect x={todayX - 22} y={2} width={44} height={14} rx={2} fill="#e53935" />
              <text x={todayX} y={12} fontSize={9} fontWeight={700} fill="#fff" textAnchor="middle">Today</text>
            </g>
          </svg>
        </div>
      </div>

      {tooltip && (
        <div className="pgv-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="pgv-tooltip-title">{tooltip.proj.name}</div>
          <div>{fmtD(tooltip.proj.startDate)} → {fmtD(tooltip.proj.endDate)}</div>
          <div>Status: {tooltip.proj.status}</div>
          <div>{Number(tooltip.proj.totalPlannedHours) || 0} h planned</div>
          <div>{Number(tooltip.proj.equivalentFteAt75) || 0} FTE @75%</div>
          <div>{clampPct(tooltip.proj.completionPercent)}% complete</div>
        </div>
      )}
    </div>
  );
};

export default PortfolioGanttView;
